import { useEffect, useState } from 'react'

import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import { useAgeVerificationContext } from '@contexts/ageVerificationContext'
import useCurrentLocationContext from '@contexts/currentLocationContext'
import { colors, fontSize, size } from '@styles/theme'
import { css, keyframes } from '@emotion/react'
import { useNavigate } from 'react-router-dom'
import useDialog from '@hooks/useDialog'
import styled from '@emotion/styled'

import TermsAndconditionsModal from '@components/legal/TermsAndConditionsModal'
import { Button, ButtonBase, Checkbox, FormLabel } from '@mui/material'
import { PiArrowRightBold } from 'react-icons/pi'
import { Emoji } from 'emoji-picker-react'
import Picker from 'react-mobile-picker'

const contract = keyframes`
  0% {
    transform: scale(3);
    opacity: 0;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
`

const EighteenPlus = styled.div`
  position: fixed;
  top: ${size(-3)};
  left: ${size(-1.5)};

  display: flex;
  align-items: center;
  justify-content: center;

  height: ${size(11)};
  width: ${size(11)};

  border-radius: 100%;
  background-color: ${colors.primary(500)};
`

const EighteenBackground = styled.div`
  position: fixed;
  top: ${size(-5)};
  left: ${size(-3.5)};
  height: ${size(15)};
  width: ${size(15)};

  background-color: ${colors.primary(950)};
  border-radius: 100%;
  opacity: 0;
  ${(props) => props.animated ? css`animation: ${contract} 1.5s ease 0.5s;` : ''}
  animation-fill-mode: forwards;
`

const EighteenText = styled.span`
  padding-left: ${size(1)};
  font-size: ${size(4)};
  font-weight: bold;
`

const AgeVerificationTitle = styled.h1`
  text-align: center;
  margin-top: ${size(10)};
  font-size: ${fontSize('xl')};
`

const AdultContentWarning = styled.p`
  margin-bottom: ${size(3)};
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  font-size: ${fontSize('sm')};
  line-height: ${size(2.25)};
  color: ${colors.primary(500)};
  max-width: ${size(40)};
  padding: 0 ${size(2)};
`

const EmojiWrapper = styled.span`
  padding-left: ${size(1)};
`

const ConsentBackground = styled(FormLabel)`
  display: flex;
  align-items: flex-start;
  position: relative;
  margin: ${size(4)} ${size(4)} 0 ${size(4)};
  padding: ${size(2)};
  background-color: ${(props) => props.meetsCriteria ? 'white' : '#282828' };
  text-decoration: ${(props) => props.meetsCriteria ? 'none' : 'line-through' };
  font-weight: 600;
  color: ${(props) => props.meetsCriteria ? 'rgba(0,0,0,0.85)' : 'grey' };
  border-radius: 5px;
  font-size: ${fontSize('sm')};
  margin-bottom: ${size(10)};
`

const ConsentText = styled.span`
  padding-left: ${size(1)};
`

const ButtonGroup = styled.div`
  position: absolute;
  bottom: 0px;
  display: flex;
  width: 100%;
  display: flex;
  background-color: black;
`

const BackButton = styled(Button)`
  flex-grow: 1;
  padding: ${size(1.5)} 0;
  margin: ${size(0.5)} 0 ${size(0.5)} ${size(0.5)};
  border-radius: 100px;
  font-weight: bold;
  color: grey;
  border-color: grey;
`

const ContinueButton = styled(Button)`
  background: ${(props) => props.acceptedTerms ? css`linear-gradient(139deg, #1976D2 23.12%, #1DE4D0 108.2%) !important` : css`#282828 !important`};
  // background: ${(props) => props.acceptedTerms ? css`linear-gradient(172.5deg, #2293EE 23.12%, #751DE4 110%)` : css`#282828 !important`};
  color: ${(props) => props.acceptedTerms ? 'white !important' : 'grey !important'};
  flex-grow: 2;
  padding: ${size(1.5)} 0;
  margin: ${size(0.5)};
  border-radius: 100px;
  font-weight: bold;
`

const DistrictText = styled.span`
  font-weight: bold;
  color: orange;
`

const AgeVerification = ({ afterConfirmationCallback, isLoading }) => {
  const { geoDistrict, isGeoDistrictRestricted } = useCurrentLocationContext()
  const [isTermsOpen, openTerms, closeTerms] = useDialog('age_verification_terms', 'age-verification-terms-open')
  const {
    setAcceptedTerms,
    meetsCriteria,
    ageSelections,
    acceptedTerms,
    setAgeValue,
    ageValue,
  } = useAgeVerificationContext()
  const navigate = useNavigate()
  const [formEl, setFormEl] = useState(null)

  // Disables the body scroll so the age verification works correctly on iphones
  // WARNING: (MAJOR WARNING) failing to undo the scroll lock prevents all iphones from scrolling until the page is refreshed!
  useEffect(() => {
    if (formEl) { disableBodyScroll(formEl) }
    return () => clearAllBodyScrollLocks()
  }, [formEl])

  const handleContinue = () => {
    if (meetsCriteria && acceptedTerms) {
      if (afterConfirmationCallback instanceof Function) {
        afterConfirmationCallback(ageValue)
      } else {
        console.error('AgeVerification.jsx: afterConfirmationCallback incorrect type or not provided')
      }
    }
  }

  const renderAdultContentText = () => {
    if (isGeoDistrictRestricted() && geoDistrict?.district) {
      return (<><DistrictText>{geoDistrict?.district}</DistrictText> requires age verification for adult content.</>)
    } else {
      return 'Mandrake contains adult content.'
    }
  }

  return (
    <form ref={(node) => node && setFormEl(node)} style={{ overflowY: 'scroll', height: '100dvh', width: '100svw', position: 'relative' }}>
      <TermsAndconditionsModal
        isOpen={isTermsOpen}
        setClosed={closeTerms}
      />
      <EighteenBackground animated />
      <EighteenPlus>
        <EighteenText>18+</EighteenText>
      </EighteenPlus>
      <AgeVerificationTitle>
        Age Check
        <EmojiWrapper>
          <Emoji unified='1f648' size='20' />
          <Emoji unified='1f382' size='20' />
        </EmojiWrapper>
      </AgeVerificationTitle>
      <AdultContentWarning>
        {renderAdultContentText()}&nbsp;Users must be 18 years or older to enter.
      </AdultContentWarning>
      <Picker value={ageValue} onChange={setAgeValue} wheelmode='normal'>
        {Object.keys(ageSelections).map(name => (
          <Picker.Column key={name} name={name}>
            {ageSelections[name].map(option => (
              <Picker.Item key={option} value={option}>
                {({ selected }) => (
                  /* Use the `selected` state to conditionally style the selected item */
                  <div style={{ color: selected ? colors.primary(500) : 'white' }}>
                    {option}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>
        ))}
      </Picker>
      <ConsentBackground meetsCriteria={meetsCriteria}>
        <Checkbox
          disabled={!meetsCriteria}
          name='acceptedTerms'
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
        />
        <ConsentText>
          I confirm <strong><u>I meet all age requirements</u></strong> outlined in the Mandrake&nbsp;
          <ButtonBase onClick={() => openTerms()} style={{ color: meetsCriteria ? colors.primary(900) : 'grey' }}>
            <strong><u>Terms of Service</u></strong>
          </ButtonBase>.
          </ConsentText>
      </ConsentBackground>
      <ButtonGroup>
        <BackButton
          size='large'
          onClick={() => navigate(-1)}
          variant='outlined'>
          Exit
        </BackButton>
        <ContinueButton
          acceptedTerms={acceptedTerms}
          onClick={handleContinue}
          disabled={!acceptedTerms || isLoading}
          variant='contained'
          size='large'>
          {isLoading ? 'Loading...' : 'Continue'}
          {!isLoading &&
            <PiArrowRightBold
              style={{ marginLeft: size(0.5) }}
              size={size(2.25)}
            />
          }
        </ContinueButton>
      </ButtonGroup>
    </form>
  )
}

export default AgeVerification
